import React, { FC, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import format from 'date-fns/format'
import isSameDay from 'date-fns/isSameDay'
import { BasicCard } from 'components/Card'
import { DailyCalendar, MonthlyCalendarContainer, OrderAmount, ToggleCalendarButton } from 'components/Calendar'
import { HorizontalLine, Spacer } from 'components/Utility'
import { BlueButton, OrangeButton } from 'components/Button'
import { State } from 'index'
import { getCurrentDate } from 'reducks/calendar/selectors'
import { getOrders } from 'reducks/checkout/selectors'
import { OrderType } from 'types/checkout'
import { parseToDate } from 'lib/helper'

type Props = {
  calendarType: 'daily' | 'monthly'
  toggleCalendar: () => void
}

const DeliveryCalendar: FC<Props> = React.memo((props) => {
  const { calendarType, toggleCalendar } = props

  const dispatch = useDispatch()
  const currentDate = useSelector((state: State) => getCurrentDate(state))
  const orders: OrderType[] = useSelector((state: State) => getOrders(state))
  const isToday = isSameDay(currentDate, new Date())

  // 取り寄せの注文数はユーザー単位でまとめない
  const orderAmount = useMemo(() => {
    return orders.filter((order) => isSameDay(parseToDate(order.date), currentDate)).length
  }, [currentDate, orders])

  const goToOrderPage = useCallback(() => {
    dispatch(push(`/order/delivery/${format(currentDate, 'yyyyMMdd')}`))
  }, [currentDate])

  const goToPreparingOrderPage = useCallback(() => {
    dispatch(push(`/order/delivery/preparing`))
  }, [])

  return (
    <BasicCard
      buttonComponent={<ToggleCalendarButton calendarType={calendarType} onClick={toggleCalendar} />}
      title={'お届け日の確認'}
    >
      {calendarType === 'daily' && <DailyCalendar />}
      {calendarType === 'monthly' && <MonthlyCalendarContainer isDeliveryCalendar={true} />}
      <HorizontalLine verticalMargin={16} />
      <OrderAmount isToday={isToday} orderAmount={orderAmount} />
      <Spacer size={12} />
      <OrangeButton
        // 注文件数が0の場合、当日の日付を選択していなければ非活性
        disabled={orderAmount === 0}
        label={'注文を確認する'}
        onClick={goToOrderPage}
        size={'medium'}
        type={'main'}
      />
      <Spacer size={12} />
      <BlueButton
        label={'未発送注文をまとめて確認する'}
        onClick={goToPreparingOrderPage}
        size={'medium'}
        type={'main'}
      />
    </BasicCard>
  )
})

export default DeliveryCalendar
